import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { AiSummaryCard } from "../components/AiSummaryCard";

const CAT_STYLE = {
  ACTION_REQUIRED: "bg-rose-100 text-rose-800 border-rose-200",
  SOURCE_UNAVAILABLE: "bg-slate-100 text-slate-600 border-slate-200",
  REVIEW: "bg-amber-100 text-amber-800 border-amber-200",
  CHANGE_DETECTED: "bg-sky-100 text-sky-800 border-sky-200",
  NO_NEW_CHANGE: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const CATS = ["ACTION_REQUIRED", "SOURCE_UNAVAILABLE", "REVIEW", "CHANGE_DETECTED", "NO_NEW_CHANGE"];
const RANK = { VIEWER: 1, ANALYST: 2, ADMIN: 3, OWNER: 4 };

function CatBadge({ cat, t, testId }) {
  return (
    <span data-testid={testId} className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${CAT_STYLE[cat] || CAT_STYLE.NO_NEW_CHANGE}`}>
      {t(`ws.cat.${cat}`)}
    </span>
  );
}

export default function PortfolioPage() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [dash, setDash] = useState(null);
  const [changes, setChanges] = useState(null);
  const [dsettings, setDsettings] = useState(null);
  const [lastDigest, setLastDigest] = useState(null);
  const [preview, setPreview] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [pdfExports, setPdfExports] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [reports, setReports] = useState([]);
  const [parcelInput, setParcelInput] = useState({ parcel_id: "", label: "" });
  const [csvText, setCsvText] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/portfolios/${id}`).then((r) => setDetail(r.data)).catch((e) => {
      if (e?.response?.status === 404) navigate("/workspace");
    });
    api.get(`/portfolios/${id}/dashboard`).then((r) => setDash(r.data)).catch(() => setDash(null));
    api.get(`/portfolios/${id}/alerts?unreviewed=true`).then((r) => setAlerts(r.data.results)).catch(() => setAlerts([]));
    api.get(`/portfolios/${id}/notes`).then((r) => setNotes(r.data.results)).catch(() => setNotes([]));
    api.get(`/portfolios/${id}/reports`).then((r) => setReports(r.data.results)).catch(() => setReports([]));
    api.get(`/portfolios/${id}/changes`).then((r) => setChanges(r.data)).catch(() => setChanges(null));
    api.get(`/portfolios/${id}/ai-summaries`).then((r) => setAiSummary(r.data.results[0] || null)).catch(() => setAiSummary(null));
    api.get(`/portfolios/${id}/pdf-exports`).then((r) => setPdfExports(r.data.results)).catch(() => setPdfExports([]));
    api.get(`/portfolios/${id}/digest-settings`).then((r) => {
      setDsettings(r.data.settings);
      setLastDigest(r.data.last_digest);
    }).catch(() => setDsettings(null));
  }, [id, navigate]);

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate("/logowanie"); return; }
    load();
  }, [user, navigate, load]);

  if (!user || !detail) return <div className="p-20 text-center text-slate-500">{t("common.loading")}</div>;

  const canWrite = (RANK[detail.my_role] || 0) >= RANK.ANALYST;
  const errText = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d?.code) return d.message || `${d.code}${d.limit != null ? ` (${d.current}/${d.limit})` : ""}`;
    return t("ws.error");
  };
  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const addParcel = () => run(async () => {
    await api.post(`/portfolios/${id}/parcels`, parcelInput);
    setParcelInput({ parcel_id: "", label: "" });
    load();
  });
  const runImport = () => run(async () => {
    const r = await api.post(`/portfolios/${id}/import`, { csv_text: csvText });
    setImportResult(r.data); setCsvText(""); load();
  });
  const archive = (ppid) => run(async () => { await api.post(`/portfolios/${id}/parcels/${ppid}/archive`); load(); });
  const restore = (ppid) => run(async () => { await api.post(`/portfolios/${id}/parcels/${ppid}/restore`); load(); });
  const review = (aid) => run(async () => { await api.post(`/portfolio-alerts/${aid}/review`); load(); });
  const addNote = () => run(async () => {
    await api.post(`/portfolios/${id}/notes`, { text: noteText });
    setNoteText(""); load();
  });
  const markReviewed = () => run(async () => {
    await api.post(`/portfolios/${id}/review`);
    load();
  });
  const saveDigestSettings = () => run(async () => {
    await api.put(`/portfolios/${id}/digest-settings`, dsettings);
    load();
  });
  const previewDigest = () => run(async () => {
    const r = await api.post(`/portfolios/${id}/digest/preview`);
    setPreview(r.data);
  });
  const sendDigest = () => run(async () => {
    await api.post(`/portfolios/${id}/digest/send`);
    load();
  });
  const genAiSummary = () => run(async () => {
    const r = await api.post(`/portfolios/${id}/ai-summary`);
    setAiSummary(r.data);
  });
  const exportPdf = (ppId) => run(async () => {
    await api.post(`/portfolios/${id}/parcels/${ppId}/pdf-export`);
    const r = await api.get(`/portfolios/${id}/pdf-exports`);
    setPdfExports(r.data.results);
  });
  const downloadPdf = async (exportId) => {
    try {
      const r = await api.get(`/pdf-exports/${exportId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PlanGmina-${exportId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(t("ws.pdf.failed"));
    }
  };
  const doExport = () => run(async () => {
    const r = await api.post(`/portfolios/${id}/export`, {}, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `plangmina-portfolio-${id.slice(0, 8)}.zip`;
    a.click();
  });

  const kpis = dash ? [
    { label: t("ws.kpi.total"), value: dash.total_parcels, testId: "kpi-total" },
    { label: t("ws.kpi.action"), value: dash.categories.ACTION_REQUIRED, testId: "kpi-action", hot: true },
    { label: t("ws.kpi.changes"), value: dash.categories.CHANGE_DETECTED, testId: "kpi-changes" },
    { label: t("ws.kpi.unavailable"), value: dash.categories.SOURCE_UNAVAILABLE + dash.findings.source_unavailable, testId: "kpi-unavailable" },
    { label: t("ws.kpi.review"), value: dash.categories.REVIEW, testId: "kpi-review" },
    { label: t("ws.kpi.reports"), value: dash.reports_generated, testId: "kpi-reports" },
  ] : [];

  return (
    <div data-testid="portfolio-page" className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <Link to="/workspace" data-testid="portfolio-back" className="text-xs text-slate-500 hover:underline">← {t("ws.back")}</Link>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{detail.name}</h1>
          <p className="mt-1 text-xs text-slate-500">
            {detail.client_ref && <>{t("ws.client_ref")}: {detail.client_ref} · </>}
            {t("ws.role")}: {detail.my_role} · {detail.status}
          </p>
        </div>
        {canWrite && (
          <Button data-testid="export-button" variant="outline" onClick={doExport} disabled={busy}>{t("ws.export")}</Button>
        )}
      </div>

      {err && <p data-testid="portfolio-error" className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>}

      {dash && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {kpis.map((k) => (
              <div key={k.testId} data-testid={k.testId} className={`rounded-xl border bg-white p-3 ${k.hot && k.value > 0 ? "border-rose-300" : ""}`}>
                <p className="text-[10px] uppercase tracking-wide text-slate-400">{k.label}</p>
                <p className={`mt-1 text-xl font-bold ${k.hot && k.value > 0 ? "text-[#DC143C]" : "text-slate-900"}`}>{k.value}</p>
              </div>
            ))}
          </div>
          <p data-testid="methodology-note" className="mt-3 text-[11px] text-slate-500">{dash.methodology_note_pl}</p>
        </>
      )}

      {changes && (
        <section data-testid="changes-section" className="mt-6 rounded-xl border bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("ws.changes.title")}</h2>
            {canWrite && (
              <div className="flex gap-2">
                <Button data-testid="digest-preview-button" size="sm" variant="outline" onClick={previewDigest} disabled={busy}>{t("ws.digest.preview")}</Button>
                <Button data-testid="digest-send-button" size="sm" variant="outline" onClick={sendDigest} disabled={busy}>{t("ws.digest.send")}</Button>
                <Button data-testid="mark-reviewed-button" size="sm" onClick={markReviewed} disabled={busy}
                  className="bg-slate-900 text-white hover:bg-slate-700">{t("ws.mark_all_reviewed")}</Button>
              </div>
            )}
          </div>
          <p className="mt-2 text-xs text-slate-600">
            <span data-testid="changes-count" className={changes.counts.changes > 0 ? "font-bold text-[#DC143C]" : "font-semibold"}>
              {changes.counts.changes} {t("ws.changes.changes")}
            </span>
            {" · "}{changes.counts.source_issues} {t("ws.changes.source_issues")}
            {" · "}{changes.counts.review_needed} {t("ws.changes.review_needed")}
            {" · "}{changes.counts.reports} {t("ws.changes.new_reports")}
            {" · "}{changes.counts.quiet} {t("ws.changes.quiet")}
          </p>
          <p className="mt-1 text-[10px] text-slate-400">
            {t("ws.changes.baseline")}: {(changes.last_reviewed_at || changes.baseline || "").slice(0, 16).replace("T", " ")}
            {lastDigest && <> · {t("ws.digest.last")}: <span data-testid="digest-last-status">{lastDigest.status}</span> {(lastDigest.created_at || "").slice(0, 16).replace("T", " ")}</>}
          </p>
          {changes.counts.changes === 0 && changes.counts.source_issues === 0 && (
            <p className="mt-2 text-xs text-emerald-700">{t("ws.changes.none")}</p>
          )}
          {changes.items.slice(0, 5).map((a) => (
            <p key={a.alert_id} data-testid={`change-item-${a.alert_id}`} className="mt-1 text-xs text-slate-700">
              <span className="font-mono text-[10px]">{a.parcel_id}</span> — <code>{a.field}</code>: {a.previous_value || "—"} → <b>{a.new_value || "—"}</b>
            </p>
          ))}
          {preview && (
            <div data-testid="digest-preview-result" className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
              <p className="font-semibold">{preview.subject}</p>
              <p className="mt-1 text-slate-500">hash: {preview.content_hash.slice(0, 16)}… · {preview.empty ? t("ws.changes.none") : JSON.stringify(preview.counts)}</p>
            </div>
          )}
          {dsettings && ["OWNER", "ADMIN"].includes(detail.my_role) && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-xs">
              <span className="font-semibold text-slate-600">{t("ws.digest.settings")}:</span>
              <label className="flex items-center gap-1">
                <input data-testid="digest-enabled-toggle" type="checkbox" checked={dsettings.enabled}
                  onChange={(e) => setDsettings({ ...dsettings, enabled: e.target.checked })} />
                {t("ws.digest.enabled")}
              </label>
              <select data-testid="digest-frequency" value={dsettings.frequency}
                onChange={(e) => setDsettings({ ...dsettings, frequency: e.target.value })}
                className="rounded-md border px-2 py-1 text-xs">
                <option value="weekly">{t("ws.digest.weekly")}</option>
                <option value="daily">{t("ws.digest.daily")}</option>
              </select>
              <Button data-testid="digest-settings-save" size="sm" variant="outline" onClick={saveDigestSettings} disabled={busy}>{t("ws.digest.save")}</Button>
            </div>
          )}
        </section>
      )}

      <AiSummaryCard summary={aiSummary} changes={changes} canWrite={canWrite} busy={busy}
        onGenerate={genAiSummary} t={t} />

      <section className="mt-8">
        <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("ws.attention")}</h2>
        <div className="mt-3 space-y-2" data-testid="attention-queue">
          {alerts.length === 0 && <p className="text-sm text-slate-500">{t("ws.no_alerts")}</p>}
          {alerts.map((a) => (
            <div key={a.id} data-testid={`alert-${a.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50/50 p-3">
              <div className="text-xs">
                <span className="font-semibold text-slate-900">{a.parcel_id}</span>
                <span className="ml-2 rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-600">{a.field}</span>
                <p className="mt-1 text-slate-600">{a.previous_value || "—"} → <b>{a.new_value || "—"}</b></p>
                <p className="mt-0.5 text-[10px] text-slate-400">{a.source_authority} · {(a.detected_at || "").slice(0, 16).replace("T", " ")}</p>
              </div>
              {canWrite && (
                <Button data-testid={`alert-review-${a.id}`} size="sm" variant="outline" onClick={() => review(a.id)} disabled={busy}>
                  {t("ws.mark_reviewed")}
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("ws.parcels")}</h2>
        {canWrite && (
          <div className="mt-3 flex flex-wrap gap-2">
            <input data-testid="add-parcel-id" value={parcelInput.parcel_id} placeholder={t("ws.parcel_id")}
              onChange={(e) => setParcelInput({ ...parcelInput, parcel_id: e.target.value })}
              className="w-64 rounded-md border px-3 py-2 text-sm" />
            <input data-testid="add-parcel-label" value={parcelInput.label} placeholder={t("ws.table.label")}
              onChange={(e) => setParcelInput({ ...parcelInput, label: e.target.value })}
              className="w-40 rounded-md border px-3 py-2 text-sm" />
            <Button data-testid="add-parcel-button" onClick={addParcel} disabled={busy || !parcelInput.parcel_id.trim()}
              className="bg-[#DC143C] text-white hover:bg-[#B91C1C]">{t("ws.add")}</Button>
          </div>
        )}
        <div className="mt-3 overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-xs" data-testid="parcels-table">
            <thead><tr className="bg-slate-900 text-left text-white">
              <th className="px-3 py-2">{t("ws.table.parcel")}</th>
              <th className="px-3 py-2">{t("ws.table.label")}</th>
              <th className="px-3 py-2">{t("ws.table.category")}</th>
              <th className="px-3 py-2">{t("ws.table.checked")}</th>
              <th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {(detail.parcels || []).map((x) => {
                const d = dash?.parcels?.find((q) => q.pp_id === x.id);
                return (
                  <tr key={x.id} data-testid={`parcel-row-${x.id}`} className={`border-t border-slate-100 ${x.archived_at ? "opacity-50" : ""}`}>
                    <td className="px-3 py-2">
                      <Link to={`/portfolio/${id}/dzialka/${x.id}/due-diligence`} data-testid={`parcel-open-${x.id}`} className="font-mono text-[11px] text-emerald-700 hover:underline">
                        {(x.identity || {}).full_number || x.parcel_id}
                      </Link>
                      {x.archived_at && <span className="ml-2 rounded bg-slate-100 px-1.5 text-[10px] text-slate-500">{t("ws.archived")}</span>}
                    </td>
                    <td className="px-3 py-2">{x.label || "—"}</td>
                    <td className="px-3 py-2">{x.archived_at ? "—" : d && <CatBadge cat={d.category} t={t} testId={`parcel-cat-${x.id}`} />}</td>
                    <td className="px-3 py-2 text-slate-500">{(x.last_checked || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/portfolio/${id}/dzialka/${x.id}/due-diligence`} data-testid={`parcel-dd-${x.id}`} className="mr-2 text-slate-600 hover:underline">{t("ws.dd.open")}</Link>
                      {canWrite && !x.archived_at && (
                        <button data-testid={`parcel-pdf-${x.id}`} onClick={() => exportPdf(x.id)} className="mr-2 text-emerald-700 hover:underline">{t("ws.pdf.export")}</button>
                      )}
                      {canWrite && (x.archived_at ? (
                        <button data-testid={`parcel-restore-${x.id}`} onClick={() => restore(x.id)} className="text-emerald-700 hover:underline">{t("ws.restore")}</button>
                      ) : (
                        <button data-testid={`parcel-archive-${x.id}`} onClick={() => archive(x.id)} className="text-slate-500 hover:underline">{t("ws.archive")}</button>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {canWrite && (
        <section className="mt-8">
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("ws.import_title")}</h2>
          <p className="mt-1 text-xs text-slate-500">{t("ws.import_hint")}</p>
          <textarea data-testid="import-csv" value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={4}
            className="mt-2 w-full rounded-md border px-3 py-2 font-mono text-xs"
            placeholder={"parcel_id; adres; etykieta\n146505_8.0103.33/3;;Klient A"} />
          <Button data-testid="import-run" onClick={runImport} disabled={busy || !csvText.trim()} variant="outline" className="mt-2">
            {t("ws.import_run")}
          </Button>
          {importResult && (
            <div data-testid="import-result" className="mt-3 rounded-xl border bg-white p-4 text-xs">
              <p>
                {t("ws.import_added")}: <b>{importResult.added}</b> · {t("ws.import_dup")}: <b>{importResult.duplicates}</b> · {t("ws.import_unresolved")}: <b className={importResult.unresolved ? "text-rose-700" : ""}>{importResult.unresolved}</b>
              </p>
              <table className="mt-2 w-full">
                <tbody>
                  {importResult.rows.map((r) => (
                    <tr key={r.row} data-testid={`import-row-${r.row}`} className="border-t border-slate-100">
                      <td className="py-1 pr-2 text-slate-400">{r.row}</td>
                      <td className="py-1 pr-2">{r.input}</td>
                      <td className={`py-1 font-semibold ${r.outcome === "added" ? "text-emerald-700" : r.outcome === "duplicate" ? "text-amber-700" : "text-rose-700"}`}>
                        {r.outcome}{r.reason ? ` (${r.reason})` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section>
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("ws.notes")}</h2>
          <div className="mt-3 space-y-2" data-testid="notes-list">
            {notes.map((n) => (
              <div key={n.id} className="rounded-xl border bg-white p-3 text-xs">
                <p className="text-slate-800">{n.text}</p>
                <p className="mt-1 text-[10px] text-slate-400">{n.user_name || n.user_id} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
              </div>
            ))}
            {notes.length === 0 && <p className="text-sm text-slate-500">{t("ws.no_notes")}</p>}
          </div>
          {canWrite && (
            <div className="mt-2 flex gap-2">
              <input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                placeholder={t("ws.note_placeholder")} className="flex-1 rounded-md border px-3 py-2 text-sm" />
              <Button data-testid="note-add" onClick={addNote} disabled={busy || !noteText.trim()} variant="outline">{t("ws.add")}</Button>
            </div>
          )}
        </section>

        <section>
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("ws.reports")}</h2>
          <div className="mt-3 overflow-x-auto rounded-xl border bg-white">
            <table className="w-full text-xs" data-testid="reports-table">
              <thead><tr className="bg-slate-900 text-left text-white">
                <th className="px-3 py-2">{t("ws.table.parcel")}</th>
                <th className="px-3 py-2">{t("ws.report_version")}</th>
                <th className="px-3 py-2">Hash</th>
                <th className="px-3 py-2">{t("ws.table.checked")}</th>
              </tr></thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} data-testid={`report-row-${r.id}`} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-[11px]">
                      <Link to={`/raport/${r.id}`} className="text-emerald-700 hover:underline">{r.parcel_id}</Link>
                    </td>
                    <td className="px-3 py-2">v{r.report_version || 1}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{(r.report_hash || "").slice(0, 12)}</td>
                    <td className="px-3 py-2 text-slate-500">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
                {reports.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-3 text-slate-500">{t("ws.no_reports")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4" data-testid="pdf-exports-section">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("ws.pdf.exports")}</h3>
            <ul className="mt-1 space-y-1">
              {pdfExports.map((e) => (
                <li key={e.id} data-testid={`pdf-export-${e.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-1.5 text-xs">
                  <span className="font-mono text-[11px]">{e.parcel_id}</span>
                  <span className="text-slate-500">v{e.report_version} · {(e.generated_at || "").slice(0, 16).replace("T", " ")}</span>
                  <button data-testid={`pdf-download-${e.id}`} onClick={() => downloadPdf(e.id)} className="text-emerald-700 hover:underline">{t("ws.pdf.download")}</button>
                </li>
              ))}
              {pdfExports.length === 0 && <li className="text-xs text-slate-400">{t("ws.pdf.none")}</li>}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
